import {
  getLockValue,
  getSettingsDataInstance,
  getShadowFabricInstance,
  unFocusObjects,
  updateBoundingBoxInfo
} from '@frontend/utils';
import { ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import parseText from '@/common/preview/parse-text';
import { FIELD_TYPE_MULTI_LINE_TEXT } from '@frontend/constants/type-fields-of-template';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { fabric } from 'fabric';
import {getGroupNameByAddFieldId} from "@/common/preview/draw-smart-object";
import {parseJsonData} from "@frontend/services/helpers";
import { getRelatedElementCoords, processMultiRelatedFields } from "@/common/preview/related-diagonal-line-helper";

const getScopePage = () => ShowPageScopeCollector.get(['canvas_data', 'updateLayoutChanges']);
export async function drawAdditionalMultiLineText({ canvas, id, previewBoundingBoxData, event}) {
  unFocusObjects();

  canvas.getObjects().forEach(function (o) {
    if (o.id.includes(ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID)
      && ((event && o.parent_element_id === event?.data?.parentElementId) || !event)
    ) {
      canvas.remove(o);
    }
  });

  /* used to save bounding box info */
  const { canvas_data } = getScopePage();
  const formData = new FormData();
  const settingsTemplate = getSettingsDataInstance(canvas_data[id]._data?.settings_template);

  formData.append('type', ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID);

  for (let index = 0; index < canvas_data[id].additionalMultiLineTexts.length; index++) {
    const field = canvas_data[id].additionalMultiLineTexts[index];
    const offset_x = canvas_data[id]._data[field + '_offset_x'] || '0';
    const offset_y = canvas_data[id]._data[field + '_offset_y'] || '0';
    const angle = canvas_data[id]._data[field + '_angle'] || '0';
    const color = canvas_data[id]._data[field + '_color'];
    const font = canvas_data[id]._data[field + '_font'] || 'Proxima-Nova-Semibold';
    const width = parseInt(canvas_data[id]._data[field + '_width']) || 300;
    const fontSize = canvas_data[id]._data[field + '_fontsize'];
    const textAlign = canvas_data[id]._data[field + '_alignment'] || 'left';
    const lineHeight = canvas_data[id]._data[field + '_leading'];
    const charSpacing = canvas_data[id]._data[field + '_tracking'];
    const x = 0;
    const y = 0;
    const text_val = canvas_data[id]._data[field];
    let additionalStyles = canvas_data[id]._data[field + '_styles'];
    const isCurved = canvas_data[id]._data[`${field}_is_curved`] || false;
    const curvedDiameter = Number(canvas_data[id]._data[`${field}_diameter`]) || 1000;
    const maxChars = Number(canvas_data[id]._data[`${field}_max_chars`] || 10000);
    const outlineShadow = parseJsonData(canvas_data[id]._data[`${field}_custom_outline_shadow`], null)
    const multiRelatedFields = parseJsonData(canvas_data[id]._data[`${field}_parent_multi_related`] || '', {})
    const multiRelatedParentElementId = canvas_data[id]._data[`${field}_parent_element_id`] || null
    const customBulletOptions = canvas_data[id]._data[`${field}_custom_bullet_options`] || null

    if (event && multiRelatedParentElementId !== event?.data?.parentElementId) {
      continue;
    }

    const curvedKerning = 1.5;
    //filter styles in case we have a broken array of styles (any empty/null values aren't allowed for fabric.styleHas method) 
    if (Array.isArray(additionalStyles)) {
      additionalStyles = additionalStyles.filter(Boolean).map(item => {
        if (Array.isArray(item)) return item.filter(Boolean)
        return item
      })
    }
    const objectId = ADDITIONAL_MULTI_LINE_TEXT_FIELD_PREFIX_ID + index;
    let fieldsOrderReassignedByUser = canvas_data[id]._data.users_fields_order;
    fieldsOrderReassignedByUser = fieldsOrderReassignedByUser ? JSON.parse(canvas_data[id]._data.users_fields_order) : false;
    const order = fieldsOrderReassignedByUser ? fieldsOrderReassignedByUser.indexOf(objectId) + 1 : 0;
    const visible = canvas_data[id]._data[field + '_visible'] ?? true;
    const name = canvas_data[id]._data[field + '_name'] 
      ? canvas_data[id]._data[field + '_name']
      : settingsTemplate.getNameById(objectId, canvas_data[id]._data[field + '_name'] || '')
      || 'Multi Line Text (add.) ' + index;
    let { styles, text } = parseText(text_val, color);
    
    if (additionalStyles && additionalStyles.length > 0) {
      styles = additionalStyles;
    }
    
    //replace inserted text with color chars to cleared text
    if (text_val && text_val !== text && text_val.includes("<c '")) {
      const { updateLayoutChanges } = getScopePage();
      updateLayoutChanges(id, field, text);
      updateLayoutChanges(id, field + '_styles', styles);
    }

    if (text !== '') {
      const textBox = new fabric.Textbox(text, {
        id: objectId,
        element_id: field,
        parent_element_id: multiRelatedParentElementId,
        top: y + parseFloat(offset_y),
        left: x + parseFloat(offset_x),
        width: width,
        fontFamily: font,
        fontSize: parseInt(fontSize),
        fill: color,
        selectable: true,
        evented: true,
        textAlign,
        lineHeight,
        charSpacing,
        fontSelector: true,
        colorSelector: true,
        fontThemeSelector: false,
        fontColor: color,
        order,
        name,
        link: settingsTemplate.getLinkById(objectId, ''),
        fieldType: FIELD_TYPE_MULTI_LINE_TEXT,
        styles,
        shadow: getShadowFabricInstance(id, field),
        locked: getLockValue(id, field),
        angle: angle ? parseFloat(angle) : 0,
        visible,
        groupName: getGroupNameByAddFieldId(
          objectId,
          canvas_data[id]
        ),
        isCurved,
        curvedDiameter,
        curvedKerning,
        maxChars,
      });

      if (outlineShadow) {
        textBox.set({
          outlineShadow: outlineShadow,
          strokeUniform: true, // Makes stroke width uniform across scaling
        });

        textBox.set({
          stroke: outlineShadow.outline.color,
          strokeWidth: +outlineShadow.outline.thickness || 0,
          strokeLineJoin: outlineShadow.outline.selectedShape,
        })

        textBox.set({
          shadow: outlineShadow.shadow
        });
      }
      
      textBox.setControlsVisibility({
        tr: true,
        br: true,
        bl: true,
        ml: true,
        mt: false,
        mr: true,
        mb: false,
        mtr: true,
      });
      
      scaleControlsCornerSize(textBox);

      textBox.set(processMuliRelatedFields(
        canvas,
        textBox,
        multiRelatedFields,
        customBulletOptions
      ))

      canvas.add(textBox);

      const boundingBox = textBox.getBoundingRect();
      const lineHeights = Math.max(...textBox.__lineHeights);
      
      formData.append('element_ids[]', field);
      formData.append(`${field}_bounding_data`, JSON.stringify(boundingBox));
      formData.append(`${field}_width`, textBox.width);
      formData.append(`${field}_height`, textBox.height);
      formData.append(`${field}_line_heights`, lineHeights.toFixed(2));
    }
  }
  // UPDATE BOUNDING BOX INFO
  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else {
    updateBoundingBoxInfo(formData, id);
  }
}

function processMuliRelatedFields(canvas, textBox, multiRelatedFields, customBulletOptions) {
  let coords = {};
  if (Object.keys(multiRelatedFields).length <= 0) {
    return coords
  }

  multiRelatedFields = processMultiRelatedFields(multiRelatedFields);
  let newCoords = {};
  let eq = true;
  let i = 0;
  do {
    newCoords = {...coords};

    for (let i = 0; i < multiRelatedFields.length; i++) {
      let relatedField = null;
      let modifyOptions = multiRelatedFields[i];

      canvas.getObjects().forEach((obj) => {
        if (obj?.templateField?.name === modifyOptions?.field) {
          relatedField = obj;
        } else if (obj?.element_id === modifyOptions?.fieldId) {
          relatedField = obj;
        }
      });

      newCoords = getRelatedElementCoords(
        newCoords,
        textBox,
        modifyOptions,
        relatedField,
        parseFloat(customBulletOptions?.width || 0) + parseFloat(customBulletOptions?.marginLeft || 0)
      );
    }

    eq = JSON.stringify(coords) === JSON.stringify(newCoords);
    coords = {...newCoords};
    i++;
  } while(!eq && i < 5);

  return coords;
}
